import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';
import { downloadDunningPdf, dunningPdfAttachment, previewDunningPdf } from '../../utils/pdf.js';
import { SendEmailModal } from './SendEmailModal.jsx';

/* Kein Rückfall auf einen fremden Absender.
 *
 * Hier standen fest verdrahtet Recicas Name und Recicas E-Mail. Solange es
 * eine Installation gab, war das ein Platzhalter. Mit mehreren Kundenfirmen
 * wird daraus ein Fehler mit Aussenwirkung: eine Firma, die ihre Angaben noch
 * nicht ausgefüllt hat, verschickt ihre Rechnung mit dem Briefkopf und der
 * Kontaktadresse eines fremden Unternehmens.
 *
 * Leer ist die ehrlichere Antwort. Wer den eigenen Namen auf der Rechnung
 * vermisst, trägt ihn in den Einstellungen nach. */

function addDays(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function DunningPrintPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [emailOpen, setEmailOpen] = useState(false);

  const notices = useQuery({
    queryKey: ['finance', 'dunning'],
    queryFn: () => financeApi.listDunning().then((r) => r.items),
  });
  const notice = (notices.data || []).find((n) => n.id === id);

  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
  });
  const invoice = useQuery({
    queryKey: ['finance', 'invoices', notice?.invoiceId],
    queryFn: () => financeApi.getInvoice(notice.invoiceId).then((r) => r.items),
    enabled: Boolean(notice?.invoiceId),
  });
  const inv = invoice.data;
  const customer = useQuery({
    queryKey: ['sales', 'customer', inv?.customerId],
    queryFn: () => salesApi.getCustomer(inv.customerId).then((r) => r.items),
    enabled: Boolean(inv?.customerId),
  });

  if (notices.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (!notice) {
    return (
      <section className="page">
        <div className="alert danger">{t('common.loadError')}</div>
        <Link to="/finance/dunning" className="btn">← {t('dunning.title')}</Link>
      </section>
    );
  }

  const cfg = settings.data || {};
  const party = customer.data;
  const amountDue = Number(notice.amountDue || 0);
  const fee = Number(notice.fee || 0);
  const total = amountDue + fee;
  const payBy = addDays(notice.noticeDate, 10);

  return (
    <div className="invoice-print-wrap">
      <div className="invoice-toolbar no-print">
        <Link to="/finance/dunning" className="btn">← {t('common.back')}</Link>
        <div className="invoice-toolbar-actions">
          <button type="button" className="btn" onClick={() => setEmailOpen(true)}>
            {t('email.send')}
          </button>
          <button type="button" className="btn" onClick={() => previewDunningPdf({ notice, invoice: inv, customer: party, settings: cfg, t })}>
            {t('pdf.preview')}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => downloadDunningPdf({ notice, invoice: inv, customer: party, settings: cfg, t })}
          >
            {t('invoice.print.downloadPdf')}
          </button>
          <button type="button" className="btn primary" onClick={() => window.print()}>
            {t('invoice.print.printOrSave')}
          </button>
        </div>
      </div>

      <article className="invoice-doc">
        <header className="invoice-doc-head">
          <div className="invoice-doc-brand">
            <div className="invoice-doc-brand-name">{cfg.companyName}</div>
            {cfg.addressLine1 && <div className="invoice-doc-muted">{cfg.addressLine1}</div>}
            {(cfg.zip || cfg.city) && (
              <div className="invoice-doc-muted">{[cfg.zip, cfg.city].filter(Boolean).join(' ')}</div>
            )}
            <div className="invoice-doc-muted">{cfg.email}</div>
            {cfg.iban && <div className="invoice-doc-muted">IBAN: {cfg.iban}</div>}
          </div>
          <div className="invoice-doc-title">
            <h1>{t('dunning.print.heading')}</h1>
            <div className="invoice-doc-number">
              {t('dunning.level')} {notice.level} · {notice.invoiceNumber}
            </div>
          </div>
        </header>

        <section className="invoice-doc-parties">
          <div>
            <div className="invoice-doc-label">{t('invoice.print.billTo')}</div>
            <div className="invoice-doc-party-name">{party?.name || '-'}</div>
            {party?.email && <div className="invoice-doc-muted">{party.email}</div>}
            {party?.code && <div className="invoice-doc-muted">{party.code}</div>}
          </div>
          <div className="invoice-doc-meta">
            <div><span>{t('dunning.col.date')}</span><strong>{formatDate(notice.noticeDate)}</strong></div>
            {inv && <div><span>{t('col.due')}</span><strong>{formatDate(inv.dueDate)}</strong></div>}
            <div><span>{t('dunning.col.invoice')}</span><strong>{notice.invoiceNumber}</strong></div>
          </div>
        </section>

        <section className="invoice-doc-notes">
          <p>{t('dunning.print.greeting')}</p>
          <p>{t('dunning.print.body')}</p>
        </section>

        <section className="invoice-doc-totals">
          <div className="invoice-doc-totals-box">
            <div><span>{t('dunning.print.amountDue')}</span><span>{formatCurrency(amountDue, notice.currency)}</span></div>
            <div><span>{t('dunning.col.fee')}</span><span>{formatCurrency(fee, notice.currency)}</span></div>
            <div className="invoice-doc-due"><span>{t('dunning.print.total')}</span><strong>{formatCurrency(total, notice.currency)}</strong></div>
          </div>
        </section>

        <section className="invoice-doc-notes">
          <p>
            <strong>{t('dunning.print.payBy')}: {formatDate(payBy)}</strong>
            {cfg.iban ? ` · IBAN: ${cfg.iban}` : ''}
          </p>
          <p>{t('dunning.print.closing')}</p>
          <p>{cfg.companyName}</p>
        </section>
      </article>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        defaultTo={party?.email}
        subject={`${t('dunning.print.heading')} ${notice.invoiceNumber}`}
        buildAttachment={() => dunningPdfAttachment({ notice, invoice: inv, customer: party, settings: cfg, t })}
      />
    </div>
  );
}
